import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, X, Plus, Minus, Trash2, ShoppingCart, ArrowLeft,
  CreditCard, DollarSign, QrCode, Loader2, Printer, Check,
  UtensilsCrossed, Hash, Percent, Tag, Pause, Play, Clock,
  Users, ReceiptText, Keyboard, Volume2,
  Wifi, WifiOff, SplitSquareVertical,
  RefreshCw, Lock, Unlock, ArrowDownCircle, ArrowUpCircle,
  FileText, Banknote, Coins, ScanBarcode, Info, ClipboardList
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useThermalPrinter } from "@/hooks/useThermalPrinter";
import type { ReceiptData, CashReportData } from "@/lib/escpos";
import type { Tables } from "@/integrations/supabase/types";

type Product = Tables<"products">;
type Category = Tables<"categories">;
type ProductAddon = Tables<"product_addons">;

interface SelectedAddon {
  addon: ProductAddon;
  quantity: number;
}

interface CartItem {
  product: Product;
  quantity: number;
  notes: string;
  addons: SelectedAddon[];
}

interface HeldOrder {
  id: string;
  name: string;
  cart: CartItem[];
  orderType: OrderType;
  tableNumber: number | null;
  customerName: string;
  createdAt: Date;
  discount: number;
  discountType: "percent" | "fixed";
}

interface ActiveTab {
  id: string;
  table_number: number;
  status: string;
  total: number;
  created_at: string;
  items: { product_name: string; quantity: number; unit_price: number }[];
}

interface CashSession {
  id: string;
  opening_balance: number;
  opened_at: string;
  status: string;
}

interface CashMovement {
  id: string;
  type: string;
  amount: number;
  description: string | null;
  created_at: string;
}

type OrderType = "takeout" | "dine_in";
type PaymentMethod = "pix" | "credit_card" | "debit_card" | "cash";

const formatCurrency = (v: number) => `R$ ${v.toFixed(2).replace(".", ",")}`;

const PDV = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const barcodeInputRef = useRef<HTMLInputElement>(null);
  const printer = useThermalPrinter();
  const [autoPrint, setAutoPrint] = useState(true);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [restaurantId, setRestaurantId] = useState<string | null>(null);
  const [restaurant, setRestaurant] = useState<any>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [allAddons, setAllAddons] = useState<ProductAddon[]>([]);

  // Cart
  const [cart, setCart] = useState<CartItem[]>([]);
  const [editingNotes, setEditingNotes] = useState<string | null>(null);

  // Addon selection dialog
  const [addonDialogProduct, setAddonDialogProduct] = useState<Product | null>(null);
  const [addonSelections, setAddonSelections] = useState<Record<string, number>>({});

  // Filters
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Order config
  const [orderType, setOrderType] = useState<OrderType>("takeout");
  const [tableNumber, setTableNumber] = useState<number | null>(null);
  const [customerName, setCustomerName] = useState("");

  // Discount
  const [discountType, setDiscountType] = useState<"percent" | "fixed">("percent");
  const [discountValue, setDiscountValue] = useState(0);
  const [showDiscountDialog, setShowDiscountDialog] = useState(false);

  // Held orders
  const [heldOrders, setHeldOrders] = useState<HeldOrder[]>([]);
  const [showHeldOrders, setShowHeldOrders] = useState(false);

  // Active tabs (table management)
  const [activeTabs, setActiveTabs] = useState<ActiveTab[]>([]);
  const [showTablesPanel, setShowTablesPanel] = useState(false);

  // Payment
  const [paymentStep, setPaymentStep] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [pixData, setPixData] = useState<{ qr_code: string; qr_code_base64: string; payment_id: number } | null>(null);
  const [pixPolling, setPixPolling] = useState(false);
  const pixPollingRef = useRef<NodeJS.Timeout | null>(null);

  // Cash change calculator
  const [showCashDialog, setShowCashDialog] = useState(false);
  const [cashReceived, setCashReceived] = useState("");
  const [pendingCashOrder, setPendingCashOrder] = useState(false);

  // Split payment
  const [showSplitPayment, setShowSplitPayment] = useState(false);
  const [splitParts, setSplitParts] = useState(2);

  // Completed order
  const [completedOrder, setCompletedOrder] = useState<any>(null);
  const [changeAmount, setChangeAmount] = useState<number | null>(null);

  // Connectivity
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Recent orders
  const [recentOrders, setRecentOrders] = useState<any[]>([]);
  const [showRecentOrders, setShowRecentOrders] = useState(false);

  // Sound
  const [soundEnabled, setSoundEnabled] = useState(true);

  // Shortcuts dialog
  const [showShortcuts, setShowShortcuts] = useState(false);

  // Time display
  const [currentTime, setCurrentTime] = useState(new Date());

  // ===== CASH REGISTER =====
  const [cashSession, setCashSession] = useState<CashSession | null>(null);
  const [cashMovements, setCashMovements] = useState<CashMovement[]>([]);
  const [showOpenCashDialog, setShowOpenCashDialog] = useState(false);
  const [showCloseCashDialog, setShowCloseCashDialog] = useState(false);
  const [showCashMovementDialog, setShowCashMovementDialog] = useState(false);
  const [showCashReportDialog, setShowCashReportDialog] = useState(false);
  const [cashMovementType, setCashMovementType] = useState<"sangria" | "suprimento">("sangria");
  const [cashMovementAmount, setCashMovementAmount] = useState("");
  const [cashMovementDesc, setCashMovementDesc] = useState("");
  const [openingBalance, setOpeningBalance] = useState("");
  const [closingBalance, setClosingBalance] = useState("");
  const [closingNotes, setClosingNotes] = useState("");

  // ===== BARCODE SCANNER =====
  const [barcodeModeActive, setBarcodeModeActive] = useState(true);
  const [lastBarcode, setLastBarcode] = useState("");
  const [barcodeFlash, setBarcodeFlash] = useState(false);
  const barcodeBufferRef = useRef("");
  const barcodeTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Barcode scanner - products ref to avoid stale closure
  const productsRef = useRef<Product[]>([]);
  useEffect(() => { productsRef.current = products; }, [products]);

  const processBarcode = useCallback((code: string) => {
    const prods = productsRef.current;
    const normalizedCode = code.toLowerCase().trim();
    
    let matched = prods.find(p => p.id.startsWith(normalizedCode));
    if (!matched) matched = prods.find(p => p.name.toLowerCase() === normalizedCode);
    if (!matched) matched = prods.find(p => p.name.toLowerCase().includes(normalizedCode));
    if (!matched) {
      const numCode = parseFloat(normalizedCode);
      if (!isNaN(numCode)) matched = prods.find(p => p.price === numCode);
    }

    if (matched) {
      const product = matched;
      setCart(prev => {
        const existing = prev.find(c => c.product.id === product.id);
        if (existing) return prev.map(c => c.product.id === product.id ? { ...c, quantity: c.quantity + 1 } : c);
        return [...prev, { product, quantity: 1, notes: "", addons: [] }];
      });
      setBarcodeFlash(true);
      setTimeout(() => setBarcodeFlash(false), 600);
      setLastBarcode("");
      if (soundEnabled) {
        try { new Audio("data:audio/wav;base64,UklGRl9vT19teleVlQEAABBB").play().catch(() => {}); } catch {}
      }
      toast({ title: `📦 ${matched.name}`, description: `Adicionado via scanner • ${formatCurrency(matched.price)}` });
    } else {
      setSearch(code);
      searchInputRef.current?.focus();
      toast({ title: "Produto não encontrado", description: `Código: ${code}`, variant: "destructive" });
    }
  }, [toast, soundEnabled]);

  // Barcode scanner detection - intercepts rapid keyboard input from USB/Bluetooth scanners
  useEffect(() => {
    if (!barcodeModeActive) return;

    const handleBarcodeInput = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;

      if (e.key === "Enter" && barcodeBufferRef.current.length >= 3) {
        e.preventDefault();
        const scannedCode = barcodeBufferRef.current.trim();
        barcodeBufferRef.current = "";
        if (barcodeTimerRef.current) clearTimeout(barcodeTimerRef.current);
        processBarcode(scannedCode);
        return;
      }

      if (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
        barcodeBufferRef.current += e.key;
        setLastBarcode(barcodeBufferRef.current);

        if (barcodeTimerRef.current) clearTimeout(barcodeTimerRef.current);
        barcodeTimerRef.current = setTimeout(() => {
          barcodeBufferRef.current = "";
          setLastBarcode("");
        }, 100);
      }
    };

    window.addEventListener("keydown", handleBarcodeInput, true);
    return () => {
      window.removeEventListener("keydown", handleBarcodeInput, true);
      if (barcodeTimerRef.current) clearTimeout(barcodeTimerRef.current);
    };
  }, [barcodeModeActive, processBarcode]);

  // Clock update
  useEffect(() => {
    const i = setInterval(() => setCurrentTime(new Date()), 30000);
    return () => clearInterval(i);
  }, []);

  // Online status
  useEffect(() => {
    const h1 = () => setIsOnline(true);
    const h2 = () => setIsOnline(false);
    window.addEventListener("online", h1);
    window.addEventListener("offline", h2);
    return () => { window.removeEventListener("online", h1); window.removeEventListener("offline", h2); };
  }, []);

  // Load data
  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate("/login"); return; }
      setUserId(user.id);
      
      const impersonateId = sessionStorage.getItem("admin_impersonate_restaurant_id");
      let rest: any = null;
      
      if (impersonateId) {
        const { data } = await supabase
          .from("restaurants").select("*").eq("id", impersonateId).maybeSingle();
        rest = data;
      } else {
        const { data } = await supabase
          .from("restaurants").select("*").eq("owner_id", user.id).maybeSingle();
        rest = data;
      }
      
      if (!rest) { navigate("/dashboard"); return; }
      setRestaurantId(rest.id);
      setRestaurant(rest);

      const [prodRes, catRes, addonsRes] = await Promise.all([
        supabase.from("products").select("*").eq("restaurant_id", rest.id).eq("is_available", true).order("sort_order"),
        supabase.from("categories").select("*").eq("restaurant_id", rest.id).eq("is_active", true).order("sort_order"),
        supabase.from("product_addons").select("*").eq("is_available", true).order("sort_order"),
      ]);
      setProducts(prodRes.data || []);
      setCategories(catRes.data || []);
      setAllAddons(addonsRes.data || []);
      setLoading(false);

      loadActiveTabs(rest.id);
      loadRecentOrders(rest.id);
      loadCashSession(rest.id);
    };
    init();
  }, [navigate]);

  // ===== CASH REGISTER FUNCTIONS =====
  const loadCashSession = async (restId: string) => {
    const { data } = await supabase
      .from("cash_register_sessions")
      .select("id, opening_balance, opened_at, status")
      .eq("restaurant_id", restId)
      .eq("status", "open")
      .order("opened_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    setCashSession(data);
    if (data) loadCashMovements(data.id);
  };

  const loadCashMovements = async (sessionId: string) => {
    const { data } = await supabase
      .from("cash_register_movements")
      .select("id, type, amount, description, created_at")
      .eq("session_id", sessionId)
      .order("created_at", { ascending: true });
    setCashMovements(data || []);
  };

  const openCashRegister = async () => {
    if (!restaurantId || !userId) return;
    const bal = parseFloat(openingBalance) || 0;
    const { data, error } = await supabase.from("cash_register_sessions").insert({
      restaurant_id: restaurantId,
      opened_by: userId,
      opening_balance: bal,
      status: "open",
    }).select("id, opening_balance, opened_at, status").single();

    if (error) {
      toast({ title: "Erro ao abrir caixa", description: error.message, variant: "destructive" });
      return;
    }
    setCashSession(data);
    setCashMovements([]);
    setOpeningBalance("");
    setShowOpenCashDialog(false);
    toast({ title: "✅ Caixa aberto!", description: `Saldo inicial: ${formatCurrency(bal)}` });
  };

  const closeCashRegister = async () => {
    if (!cashSession || !userId || !restaurantId) return;
    const bal = parseFloat(closingBalance) || 0;

    // Calculate expected balance
    const expected = computeExpectedBalance();

    await supabase.from("cash_register_sessions").update({
      status: "closed",
      closed_by: userId,
      closing_balance: bal,
      expected_balance: expected,
      closed_at: new Date().toISOString(),
      notes: closingNotes || null,
    }).eq("id", cashSession.id);

    setCashSession(null);
    setCashMovements([]);
    setClosingBalance("");
    setClosingNotes("");
    setShowCloseCashDialog(false);
    toast({ title: "🔒 Caixa fechado!", description: `Saldo informado: ${formatCurrency(bal)}` });
  };

  const addCashMovement = async () => {
    if (!cashSession || !userId || !restaurantId) return;
    const amt = parseFloat(cashMovementAmount) || 0;
    if (amt <= 0) {
      toast({ title: "Informe um valor válido", variant: "destructive" });
      return;
    }

    const { error } = await supabase.from("cash_register_movements").insert({
      session_id: cashSession.id,
      restaurant_id: restaurantId,
      type: cashMovementType,
      amount: amt,
      description: cashMovementDesc || (cashMovementType === "sangria" ? "Sangria" : "Suprimento"),
      created_by: userId,
    });

    if (error) {
      toast({ title: "Erro ao registrar movimentação", description: error.message, variant: "destructive" });
      return;
    }

    loadCashMovements(cashSession.id);
    setCashMovementAmount("");
    setCashMovementDesc("");
    setShowCashMovementDialog(false);
    toast({
      title: cashMovementType === "sangria" ? "💸 Sangria registrada" : "💰 Suprimento registrado",
      description: formatCurrency(amt),
    });
  };

  const computeExpectedBalance = () => {
    if (!cashSession) return 0;
    let balance = cashSession.opening_balance;
    for (const m of cashMovements) {
      if (m.type === "sale" || m.type === "suprimento") balance += m.amount;
      else if (m.type === "sangria" || m.type === "change_given") balance -= m.amount;
    }
    return balance;
  };

  const cashSummary = useMemo(() => {
    let totalSales = 0;
    let totalSangria = 0;
    let totalSuprimento = 0;
    let totalChangeGiven = 0;
    let salesCount = 0;

    for (const m of cashMovements) {
      if (m.type === "sale") { totalSales += m.amount; salesCount++; }
      if (m.type === "sangria") totalSangria += m.amount;
      if (m.type === "suprimento") totalSuprimento += m.amount;
      if (m.type === "change_given") totalChangeGiven += m.amount;
    }

    const opening = cashSession?.opening_balance || 0;
    const expected = opening + totalSales + totalSuprimento - totalSangria - totalChangeGiven;

    return { totalSales, totalSangria, totalSuprimento, totalChangeGiven, salesCount, opening, expected };
  }, [cashMovements, cashSession]);

  // ===== END CASH REGISTER =====

  const loadActiveTabs = async (restId: string) => {
    const { data } = await supabase
      .from("table_tabs")
      .select("id, table_number, status, total, created_at")
      .eq("restaurant_id", restId)
      .eq("status", "open")
      .order("table_number");
    if (data) {
      const tabsWithItems = await Promise.all(data.map(async (tab) => {
        const { data: items } = await supabase
          .from("table_tab_items")
          .select("product_name, quantity, unit_price")
          .eq("tab_id", tab.id);
        return { ...tab, items: items || [] };
      }));
      setActiveTabs(tabsWithItems);
    }
  };

  const loadRecentOrders = async (restId: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const { data } = await supabase
      .from("orders")
      .select("id, customer_name, total, status, payment_method, order_type, table_number, created_at")
      .eq("restaurant_id", restId)
      .gte("created_at", today.toISOString())
      .order("created_at", { ascending: false })
      .limit(20);
    setRecentOrders(data || []);
  };

  // Cleanup PIX polling
  useEffect(() => {
    return () => {
      if (pixPollingRef.current) clearInterval(pixPollingRef.current);
    };
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.tagName === "INPUT" || (e.target as HTMLElement)?.tagName === "TEXTAREA") {
        if (e.key === "Escape") (e.target as HTMLElement).blur();
        return;
      }

      if (e.key === "F1") { e.preventDefault(); setShowShortcuts(true); }
      if (e.key === "F2") { e.preventDefault(); searchInputRef.current?.focus(); }
      if (e.key === "F3") { e.preventDefault(); setShowHeldOrders(true); }
      if (e.key === "F4") { e.preventDefault(); setShowTablesPanel(true); }
      if (e.key === "F5") { e.preventDefault(); setShowRecentOrders(true); }
      if (e.key === "F6") { e.preventDefault(); setShowCashReportDialog(true); }
      if (e.key === "F7") { e.preventDefault(); barcodeInputRef.current?.focus(); }
      if (e.key === "F8") { e.preventDefault(); if (cart.length > 0) setShowDiscountDialog(true); }
      if (e.key === "F9") {
        e.preventDefault();
        if (cart.length > 0) {
          if (orderType === "dine_in" && !tableNumber) {
            toast({ title: "Informe o número da mesa", variant: "destructive" });
            return;
          }
          setPaymentStep(true);
        }
      }
      if (e.key === "F10") { e.preventDefault(); holdCurrentOrder(); }
      if (e.key === "Escape") {
        if (showCashDialog) { setShowCashDialog(false); setCashReceived(""); }
        else if (paymentStep) setPaymentStep(false);
        else if (showDiscountDialog) setShowDiscountDialog(false);
      }
      if (paymentStep && !processingPayment && !showCashDialog) {
        if (e.key === "1") handlePayment("cash");
        if (e.key === "2") handlePayment("credit_card");
        if (e.key === "3") handlePayment("debit_card");
        if (e.key === "4") handlePayment("pix");
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [cart, paymentStep, processingPayment, orderType, tableNumber, showCashDialog]);

  // Filtered products
  const filteredProducts = useMemo(() => {
    let result = products;
    if (selectedCategory) result = result.filter(p => p.category_id === selectedCategory);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(p => p.name.toLowerCase().includes(q) || p.description?.toLowerCase().includes(q));
    }
    return result;
  }, [products, selectedCategory, search]);

  // Get addons for a product
  const getProductAddons = useCallback((productId: string) => {
    return allAddons.filter(a => a.product_id === productId && a.is_available);
  }, [allAddons]);

  // Cart helpers
  const addToCart = useCallback((product: Product) => {
    const productAddons = allAddons.filter(a => a.product_id === product.id && a.is_available);
    if (productAddons.length > 0) {
      // Open addon selection dialog
      setAddonDialogProduct(product);
      setAddonSelections({});
      return;
    }
    // No addons - add directly
    setCart(prev => {
      const existing = prev.find(c => c.product.id === product.id && c.addons.length === 0);
      if (existing) return prev.map(c => c.product.id === product.id && c.addons.length === 0 ? { ...c, quantity: c.quantity + 1 } : c);
      return [...prev, { product, quantity: 1, notes: "", addons: [] }];
    });
    if (soundEnabled) {
      try { new Audio("data:audio/wav;base64,UklGRl9vT19teleVlQEAABBB").play().catch(() => {}); } catch {}
    }
  }, [allAddons, soundEnabled]);

  // Confirm addon selection and add to cart
  const confirmAddonsToCart = useCallback(() => {
    if (!addonDialogProduct) return;
    const selected: SelectedAddon[] = Object.entries(addonSelections)
      .filter(([, qty]) => qty > 0)
      .map(([id, qty]) => ({ addon: allAddons.find(a => a.id === id)!, quantity: qty }))
      .filter(s => s.addon);
    
    setCart(prev => [...prev, { product: addonDialogProduct, quantity: 1, notes: "", addons: selected }]);
    setAddonDialogProduct(null);
    setAddonSelections({});
    if (soundEnabled) {
      try { new Audio("data:audio/wav;base64,UklGRl9vT19teleVlQEAABBB").play().catch(() => {}); } catch {}
    }
  }, [addonDialogProduct, addonSelections, allAddons, soundEnabled]);

  const getCartItemTotal = (c: CartItem) => {
    const addonTotal = c.addons.reduce((sum, a) => sum + a.addon.price * a.quantity, 0);
    return (c.product.price + addonTotal) * c.quantity;
  };

  const updateQty = (idx: number, delta: number) => {
    setCart(prev => prev.map((c, i) => {
      if (i !== idx) return c;
      const newQty = c.quantity + delta;
      return newQty > 0 ? { ...c, quantity: newQty } : c;
    }).filter(c => c.quantity > 0));
  };

  const removeFromCart = (idx: number) => setCart(prev => prev.filter((_, i) => i !== idx));

  const updateNotes = (idx: number, notes: string) => {
    setCart(prev => prev.map((c, i) => i === idx ? { ...c, notes } : c));
  };

  const subtotal = cart.reduce((sum, c) => sum + getCartItemTotal(c), 0);
  const discountAmount = discountType === "percent" ? subtotal * (discountValue / 100) : discountValue;
  const total = Math.max(0, subtotal - discountAmount);

  // Hold order
  const holdCurrentOrder = () => {
    if (cart.length === 0) return;
    const held: HeldOrder = {
      id: crypto.randomUUID(),
      name: customerName || `Pedido ${heldOrders.length + 1}`,
      cart: [...cart],
      orderType,
      tableNumber,
      customerName,
      createdAt: new Date(),
      discount: discountValue,
      discountType,
    };
    setHeldOrders(prev => [...prev, held]);
    setCart([]);
    setCustomerName("");
    setDiscountValue(0);
    setTableNumber(null);
    toast({ title: "⏸ Pedido em espera", description: held.name });
  };

  const resumeHeldOrder = (id: string) => {
    const held = heldOrders.find(h => h.id === id);
    if (!held) return;
    if (cart.length > 0) holdCurrentOrder();
    setCart(held.cart);
    setOrderType(held.orderType);
    setTableNumber(held.tableNumber);
    setCustomerName(held.customerName);
    setDiscountValue(held.discount);
    setDiscountType(held.discountType);
    setHeldOrders(prev => prev.filter(h => h.id !== id));
    setShowHeldOrders(false);
  };

  const removeHeldOrder = (id: string) => {
    setHeldOrders(prev => prev.filter(h => h.id !== id));
  };

  // Send cart items to a table tab (comanda) AND create a kitchen order
  const sendToTab = async () => {
    if (!restaurantId || cart.length === 0 || !tableNumber) return;

    setProcessingPayment(true);

    try {
      // Check if there's already an open tab for this table
      const { data: existingTab } = await supabase
        .from("table_tabs")
        .select("id, total")
        .eq("restaurant_id", restaurantId)
        .eq("table_number", tableNumber)
        .eq("status", "open")
        .maybeSingle();

      let tabId: string;
      let currentTotal = 0;

      if (existingTab) {
        tabId = existingTab.id;
        currentTotal = existingTab.total;
      } else {
        // Create new tab
        const { data: newTab, error: tabError } = await supabase
          .from("table_tabs")
          .insert({
            restaurant_id: restaurantId,
            table_number: tableNumber,
            status: "open",
            total: 0,
          })
          .select("id")
          .single();

        if (tabError || !newTab) {
          toast({ title: "Erro ao abrir comanda", description: tabError?.message, variant: "destructive" });
          setProcessingPayment(false);
          return;
        }
        tabId = newTab.id;
      }

      // Insert items into table_tab_items
      const tabItems = cart.map(c => {
        const addonTotal = c.addons.reduce((sum, a) => sum + a.addon.price * a.quantity, 0);
        const addonJson = c.addons.length > 0
          ? c.addons.map(a => ({ name: a.addon.name, quantity: a.quantity, price: a.addon.price }))
          : null;
        return {
          tab_id: tabId,
          product_id: c.product.id,
          product_name: c.product.name,
          quantity: c.quantity,
          unit_price: c.product.price + addonTotal,
          notes: c.notes || null,
          addons: addonJson,
        };
      });

      const { error: itemsError } = await supabase.from("table_tab_items").insert(tabItems);
      if (itemsError) {
        toast({ title: "Erro ao adicionar itens", description: itemsError.message, variant: "destructive" });
        setProcessingPayment(false);
        return;
      }

      // Update tab total
      const cartSubtotal = cart.reduce((sum, c) => {
        const addonTotal = c.addons.reduce((s, a) => s + a.addon.price * a.quantity, 0);
        return sum + (c.product.price + addonTotal) * c.quantity;
      }, 0);
      const newTotal = currentTotal + cartSubtotal;
      await supabase.from("table_tabs").update({ total: newTotal }).eq("id", tabId);

      // Create an order so it appears in the kitchen panel
      const { data: order, error: orderError } = await supabase.from("orders").insert({
        restaurant_id: restaurantId,
        order_type: "dine_in" as any,
        table_number: tableNumber,
        customer_name: customerName || `Mesa ${tableNumber}`,
        payment_method: null,
        payment_status: "pending",
        subtotal: cartSubtotal,
        delivery_fee: 0,
        discount: 0,
        total: cartSubtotal,
        status: "received" as any,
        notes: `PDV - Comanda Mesa ${tableNumber}`,
      }).select().single();

      if (order) {
        const orderItems = cart.map(c => {
          const addonTotal = c.addons.reduce((s, a) => s + a.addon.price * a.quantity, 0);
          const addonNotes = c.addons.map(a => `+ ${a.quantity}x ${a.addon.name} (${formatCurrency(a.addon.price * a.quantity)})`).join("; ");
          const fullNotes = [c.notes, addonNotes].filter(Boolean).join(" | ");
          return {
            order_id: order.id,
            product_id: c.product.id,
            product_name: c.product.name,
            quantity: c.quantity,
            unit_price: c.product.price + addonTotal,
            total_price: (c.product.price + addonTotal) * c.quantity,
            notes: fullNotes || null,
          };
        });
        await supabase.from("order_items").insert(orderItems);
      }

      // Reset cart
      setCart([]);
      setCustomerName("");
      setDiscountValue(0);
      setTableNumber(null);
      setOrderType("takeout");
      setProcessingPayment(false);

      if (restaurantId) loadActiveTabs(restaurantId);

      toast({
        title: existingTab ? "✅ Itens adicionados à comanda!" : "✅ Comanda aberta!",
        description: `Mesa ${tableNumber} • ${formatCurrency(newTotal)}`,
      });
    } catch (err) {
      toast({ title: "Erro ao processar comanda", variant: "destructive" });
      setProcessingPayment(false);
    }
  };

  // Create order
  const createOrder = async (pm: PaymentMethod) => {
    if (!restaurantId || cart.length === 0) return null;

    const { data: order, error } = await supabase.from("orders").insert({
      restaurant_id: restaurantId,
      order_type: orderType,
      table_number: orderType === "dine_in" ? tableNumber : null,
      customer_name: customerName || "Balcão",
      payment_method: pm,
      payment_status: pm === "pix" ? "pending" : "paid",
      subtotal,
      delivery_fee: 0,
      discount: discountAmount,
      total,
      status: "received",
      notes: `PDV - ${orderType === "dine_in" ? `Mesa ${tableNumber}` : "Balcão"}${discountAmount > 0 ? ` | Desconto: ${formatCurrency(discountAmount)}` : ""}`,
    }).select().single();

    if (error || !order) {
      toast({ title: "Erro ao criar pedido", description: error?.message, variant: "destructive" });
      return null;
    }

    const items = cart.map(c => {
      const addonTotal = c.addons.reduce((sum, a) => sum + a.addon.price * a.quantity, 0);
      const addonNotes = c.addons.map(a => `+ ${a.quantity}x ${a.addon.name} (${formatCurrency(a.addon.price * a.quantity)})`).join("; ");
      const fullNotes = [c.notes, addonNotes].filter(Boolean).join(" | ");
      return {
        order_id: order.id,
        product_id: c.product.id,
        product_name: c.product.name,
        quantity: c.quantity,
        unit_price: c.product.price + addonTotal,
        total_price: (c.product.price + addonTotal) * c.quantity,
        notes: fullNotes || null,
      };
    });

    await supabase.from("order_items").insert(items);

    // Record sale in cash register
    if (cashSession && userId && restaurantId && pm === "cash") {
      await supabase.from("cash_register_movements").insert({
        session_id: cashSession.id,
        restaurant_id: restaurantId,
        type: "sale",
        amount: total,
        description: `Venda #${order.id.slice(0, 8)}`,
        created_by: userId,
        order_id: order.id,
      });
    }

    return { ...order, items };
  };

  // Handle payment
  const handlePayment = async (method: PaymentMethod) => {
    // For cash: show change calculator dialog first
    if (method === "cash") {
      setCashReceived("");
      setShowCashDialog(true);
      setPendingCashOrder(true);
      return;
    }

    setPaymentMethod(method);
    setProcessingPayment(true);

    if (method === "pix") {
      const order = await createOrder("pix");
      if (!order) { setProcessingPayment(false); return; }

      try {
        const { data, error } = await supabase.functions.invoke("process-mp-payment", {
          body: {
            action: "create_pix",
            restaurant_id: restaurantId,
            transaction_amount: total,
            payer_email: "pdv@loja.com",
            payer_first_name: customerName || "Cliente",
            order_data: {
              customer_name: customerName || "Cliente PDV",
              order_type: orderType,
              table_number: orderType === "dine_in" ? tableNumber : null,
              notes: `PDV`,
              items: cart.map(c => ({
                productId: c.product.id,
                name: c.product.name,
                price: c.product.price,
                qty: c.quantity,
                addons: [],
              })),
              discount: discountAmount,
            },
          },
        });

        if (error || !data?.success || !data?.qr_code_base64) {
          toast({ title: "Erro ao gerar PIX", description: data?.error_message || "Tente novamente", variant: "destructive" });
          await supabase.from("order_items").delete().eq("order_id", order.id);
          await supabase.from("orders").delete().eq("id", order.id);
          setProcessingPayment(false);
          return;
        }

        setPixData({ qr_code: data.qr_code, qr_code_base64: data.qr_code_base64, payment_id: data.payment_id });
        setProcessingPayment(false);

        setPixPolling(true);
        pixPollingRef.current = setInterval(async () => {
          const { data: statusData } = await supabase.functions.invoke("process-mp-payment", {
            body: { action: "check_pix_status", payment_id: data.payment_id, restaurant_id: restaurantId },
          });
          if (statusData?.status === "approved") {
            clearInterval(pixPollingRef.current!);
            setPixPolling(false);
            await supabase.from("orders").update({ payment_status: "paid" }).eq("id", order.id);
            setPixData(null);
            finishOrder(order, null);
            toast({ title: "✅ Pagamento PIX confirmado!" });
          }
        }, 5000);
      } catch {
        toast({ title: "Erro ao processar PIX", variant: "destructive" });
        setProcessingPayment(false);
      }
    } else {
      // credit_card, debit_card
      const order = await createOrder(method);
      if (!order) { setProcessingPayment(false); return; }
      setProcessingPayment(false);
      finishOrder(order, null);
      toast({ title: "✅ Pedido criado com sucesso!" });
    }
  };

  // Confirm cash payment
  const confirmCashPayment = async () => {
    const received = parseFloat(cashReceived) || 0;
    if (received < total) {
      toast({ title: "Valor insuficiente", description: `Mínimo: ${formatCurrency(total)}`, variant: "destructive" });
      return;
    }

    const change = received - total;
    setShowCashDialog(false);
    setPendingCashOrder(false);
    setPaymentMethod("cash");
    setProcessingPayment(true);

    const order = await createOrder("cash");
    if (!order) { setProcessingPayment(false); return; }

    // Record change given in cash register
    if (change > 0 && cashSession && userId && restaurantId) {
      await supabase.from("cash_register_movements").insert({
        session_id: cashSession.id,
        restaurant_id: restaurantId,
        type: "change_given",
        amount: change,
        description: `Troco pedido #${order.id.slice(0, 8)}`,
        created_by: userId,
        order_id: order.id,
      });
    }

    setProcessingPayment(false);
    finishOrder(order, change > 0 ? change : null);
    toast({ title: "✅ Pedido criado com sucesso!" });
  };

  const finishOrder = (order: any, change: number | null) => {
    const orderItems = cart.map(c => {
      const addonTotal = c.addons.reduce((sum, a) => sum + a.addon.price * a.quantity, 0);
      const addonDesc = c.addons.map(a => `+${a.quantity}x ${a.addon.name}`).join(", ");
      return {
        product_name: addonDesc ? `${c.product.name} (${addonDesc})` : c.product.name,
        quantity: c.quantity,
        unit_price: c.product.price + addonTotal,
        total_price: (c.product.price + addonTotal) * c.quantity,
      };
    });
    setCompletedOrder({
      ...order,
      items: orderItems,
    });
    setChangeAmount(change);

    // Auto-print receipt
    if (autoPrint && restaurant) {
      const receiptData: ReceiptData = {
        restaurant: { name: restaurant.name, phone: restaurant.phone || undefined },
        orderId: order.id,
        orderType: order.order_type,
        tableNumber: order.table_number,
        customer: { name: order.customer_name },
        paymentMethod: order.payment_method,
        items: orderItems.map(i => ({ name: i.product_name, quantity: i.quantity, unitPrice: i.unit_price, totalPrice: i.total_price })),
        subtotal: order.subtotal,
        discount: order.discount,
        total: order.total,
        changeAmount: change,
        createdAt: order.created_at,
      };
      printer.printReceipt(receiptData);
    }

    setCart([]);
    setPaymentStep(false);
    setDiscountValue(0);
    setCustomerName("");
    setCashReceived("");
    if (restaurantId) {
      loadRecentOrders(restaurantId);
      loadActiveTabs(restaurantId);
      if (cashSession) loadCashMovements(cashSession.id);
    }
  };

  // Print receipt (manual)
  const handlePrintReceipt = (orderData?: any) => {
    const ord = orderData || completedOrder;
    if (!ord || !restaurant) return;
    const items = ord.items || [];
    const receiptData: ReceiptData = {
      restaurant: { name: restaurant.name, phone: restaurant.phone || undefined },
      orderId: ord.id,
      orderType: ord.order_type,
      tableNumber: ord.table_number,
      customer: { name: ord.customer_name },
      paymentMethod: ord.payment_method,
      items: items.map((i: any) => ({ name: i.product_name, quantity: i.quantity, unitPrice: i.unit_price, totalPrice: i.total_price })),
      subtotal: ord.subtotal,
      deliveryFee: ord.delivery_fee,
      discount: ord.discount,
      total: ord.total,
      changeAmount: changeAmount,
      createdAt: ord.created_at,
      notes: ord.notes,
    };
    printer.printReceipt(receiptData);
  };

  // Print closing report
  const printClosingReport = () => {
    if (!restaurant || !cashSession) return;
    const diff = parseFloat(closingBalance || "0") - cashSummary.expected;
    const reportData: CashReportData = {
      restaurantName: restaurant.name,
      openedAt: new Date(cashSession.opened_at).toLocaleString("pt-BR"),
      closedAt: new Date().toLocaleString("pt-BR"),
      openingBalance: cashSummary.opening,
      totalSales: cashSummary.totalSales,
      salesCount: cashSummary.salesCount,
      totalSangria: cashSummary.totalSangria,
      totalSuprimento: cashSummary.totalSuprimento,
      totalChangeGiven: cashSummary.totalChangeGiven,
      expectedBalance: cashSummary.expected,
      closingBalance: parseFloat(closingBalance || "0"),
      difference: diff,
    };
    printer.printCashReport(reportData);
  };

  const newOrder = () => {
    setCompletedOrder(null);
    setPaymentStep(false);
    setPaymentMethod(null);
    setDiscountValue(0);
    setChangeAmount(null);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Completed order screen
  if (completedOrder) {
    return (
      <div className="fixed inset-0 bg-background flex items-center justify-center p-8">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-card border border-border rounded-2xl shadow-2xl max-w-md w-full p-8 text-center space-y-6"
        >
          <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto">
            <Check className="h-10 w-10 text-emerald-500" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">Pedido Criado!</h2>
            <p className="text-muted-foreground mt-1">
              #{completedOrder.id.slice(0, 8).toUpperCase()} •{" "}
              {completedOrder.order_type === "dine_in" ? `Mesa ${completedOrder.table_number}` : "Balcão"}
            </p>
            {completedOrder.customer_name && completedOrder.customer_name !== "Balcão" && (
              <p className="text-sm text-muted-foreground">{completedOrder.customer_name}</p>
            )}
            {completedOrder.discount > 0 && (
              <p className="text-sm text-emerald-600 mt-1">Desconto: -{formatCurrency(completedOrder.discount)}</p>
            )}
            <p className="text-3xl font-bold mt-4">{formatCurrency(completedOrder.total)}</p>

            {/* Change display */}
            {changeAmount !== null && changeAmount > 0 && (
              <div className="mt-3 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Coins className="h-5 w-5 text-amber-600" />
                  <span className="text-sm font-semibold text-amber-700">Troco</span>
                </div>
                <p className="text-2xl font-bold text-amber-600">{formatCurrency(changeAmount)}</p>
              </div>
            )}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => handlePrintReceipt()}>
              <Printer className="h-4 w-4 mr-2" />
              Imprimir
            </Button>
            <Button className="flex-1 bg-primary text-primary-foreground" onClick={newOrder}>
              Novo Pedido
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  // PIX QR Code Screen
  if (pixData) {
    return (
      <div className="fixed inset-0 bg-background flex items-center justify-center p-8">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-card border border-border rounded-2xl shadow-2xl max-w-sm w-full p-8 text-center space-y-6"
        >
          <h2 className="text-xl font-bold">Pagamento PIX</h2>
          <p className="text-3xl font-bold">{formatCurrency(total)}</p>
          <div className="bg-white p-4 rounded-xl inline-block mx-auto">
            <img src={`data:image/png;base64,${pixData.qr_code_base64}`} alt="QR Code PIX" className="w-52 h-52" />
          </div>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Escaneie o QR Code com o app do banco</p>
            {pixData.qr_code && (
              <Button variant="outline" size="sm" onClick={() => {
                navigator.clipboard.writeText(pixData.qr_code);
                toast({ title: "Código PIX copiado!" });
              }}>
                Copiar código PIX
              </Button>
            )}
            {pixPolling && (
              <div className="flex items-center justify-center gap-2 text-sm text-primary">
                <Loader2 className="h-4 w-4 animate-spin" />
                Aguardando pagamento...
              </div>
            )}
          </div>
          <Button variant="outline" onClick={() => {
            if (pixPollingRef.current) clearInterval(pixPollingRef.current);
            setPixData(null); setPixPolling(false); setPaymentStep(false);
          }}>
            Cancelar
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-background flex flex-col">
      {/* Status bar */}
      <div className="h-8 bg-card border-b border-border flex items-center px-4 gap-3 text-xs shrink-0">
        <span className="font-medium text-foreground">{restaurant?.name}</span>
        <div className="flex-1" />

        {/* Cash register status */}
        {cashSession ? (
          <button
            onClick={() => setShowCashReportDialog(true)}
            className="flex items-center gap-1 text-emerald-600 hover:text-emerald-500"
          >
            <Unlock className="h-3 w-3" />
            Caixa Aberto
          </button>
        ) : (
          <button
            onClick={() => setShowOpenCashDialog(true)}
            className="flex items-center gap-1 text-amber-500 hover:text-amber-400"
          >
            <Lock className="h-3 w-3" />
            Caixa Fechado
          </button>
        )}

        <div className="w-px h-4 bg-border" />

        <button
          onClick={() => setBarcodeModeActive(!barcodeModeActive)}
          className={`flex items-center gap-1 text-xs ${barcodeModeActive ? "text-emerald-600 hover:text-emerald-500" : "text-muted-foreground hover:text-foreground"}`}
          title={barcodeModeActive ? "Scanner ativo - clique para desativar" : "Scanner inativo - clique para ativar"}
        >
          <ScanBarcode className={`h-3 w-3 ${barcodeFlash ? "animate-pulse text-primary" : ""}`} />
          {barcodeModeActive ? "Scanner" : "Scanner Off"}
        </button>

        <div className="w-px h-4 bg-border" />

        {printer.isSupported ? (
          <button
            onClick={() => printer.connected ? printer.disconnect() : printer.connect()}
            className={`flex items-center gap-1 text-xs ${printer.connected ? "text-emerald-600" : "text-muted-foreground hover:text-foreground"}`}
            title={printer.connected ? `${printer.printerName} - Clique para desconectar` : "Conectar impressora térmica USB"}
          >
            <Printer className="h-3 w-3" />
            {printer.connected ? "USB" : "Impressora"}
          </button>
        ) : (
          <span
            className="flex items-center gap-1 text-xs text-amber-500"
            title="Web Serial API indisponível neste navegador. A impressão será feita via diálogo do navegador (window.print). Para conexão USB direta, use o Google Chrome no app publicado."
          >
            <Info className="h-3 w-3" />
            Impressão via navegador
          </span>
        )}

        <button
          onClick={() => setAutoPrint(!autoPrint)}
          className={`flex items-center gap-1 text-xs ${autoPrint ? "text-emerald-600" : "text-muted-foreground"}`}
          title={autoPrint ? "Auto-impressão ativa" : "Auto-impressão desativada"}
        >
          {autoPrint ? "Auto ✓" : "Auto ✗"}
        </button>

        {heldOrders.length > 0 && (
          <button onClick={() => setShowHeldOrders(true)} className="flex items-center gap-1 text-amber-500 hover:text-amber-400">
            <Pause className="h-3 w-3" />
            {heldOrders.length} em espera
          </button>
        )}
        <span className="text-muted-foreground flex items-center gap-1">
          {isOnline ? <Wifi className="h-3 w-3 text-emerald-500" /> : <WifiOff className="h-3 w-3 text-destructive" />}
          {isOnline ? "Online" : "Offline"}
        </span>
        <button onClick={() => setSoundEnabled(!soundEnabled)} className="text-muted-foreground hover:text-foreground">
          <Volume2 className={`h-3 w-3 ${!soundEnabled ? "opacity-30" : ""}`} />
        </button>
        <span className="text-muted-foreground">
          {currentTime.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
        </span>
        <button onClick={() => setShowShortcuts(true)} className="text-muted-foreground hover:text-foreground">
          <Keyboard className="h-3 w-3" />
        </button>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Left: Products */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Top bar */}
          <header className="h-12 border-b border-border flex items-center px-4 gap-3 shrink-0">
            <button onClick={() => navigate("/dashboard")} className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
                <ShoppingCart className="h-4 w-4 text-primary-foreground" />
              </div>
              <h1 className="font-bold text-lg">PDV</h1>
            </div>
            <div className="flex-1" />

            {/* Cash register actions */}
            {cashSession && (
              <>
                <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => {
                  setCashMovementType("sangria");
                  setShowCashMovementDialog(true);
                }}>
                  <ArrowDownCircle className="h-3.5 w-3.5 mr-1 text-destructive" />
                  Sangria
                </Button>
                <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => {
                  setCashMovementType("suprimento");
                  setShowCashMovementDialog(true);
                }}>
                  <ArrowUpCircle className="h-3.5 w-3.5 mr-1 text-emerald-600" />
                  Suprimento
                </Button>
                <div className="w-px h-6 bg-border" />
              </>
            )}

            <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => setShowTablesPanel(true)}>
              <Users className="h-3.5 w-3.5 mr-1" />
              Mesas
              {activeTabs.length > 0 && (
                <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">{activeTabs.length}</Badge>
              )}
            </Button>
            <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => setShowRecentOrders(true)}>
              <ReceiptText className="h-3.5 w-3.5 mr-1" />
              Pedidos
            </Button>

            <div className="w-px h-6 bg-border" />

            {/* Order type toggle */}
            <div className="flex bg-secondary rounded-lg p-0.5">
              <button
                onClick={() => setOrderType("takeout")}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                  orderType === "takeout" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"
                }`}
              >
                🥡 Balcão
              </button>
              <button
                onClick={() => setOrderType("dine_in")}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                  orderType === "dine_in" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"
                }`}
              >
                🍽️ Mesa
              </button>
            </div>

            {orderType === "dine_in" && (
              <div className="flex items-center gap-1">
                <Hash className="h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  type="number"
                  placeholder="Nº"
                  className="w-14 h-7 text-center text-xs"
                  value={tableNumber || ""}
                  onChange={(e) => setTableNumber(Number(e.target.value) || null)}
                />
              </div>
            )}
          </header>

          <div className="px-4 py-2 space-y-2 shrink-0">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  ref={searchInputRef}
                  placeholder="Buscar produto... (F2)"
                  className="pl-10 h-9"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2">
                    <X className="h-4 w-4 text-muted-foreground" />
                  </button>
                )}
              </div>
              {/* Manual barcode input */}
              <div className="relative w-48">
                <ScanBarcode className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  ref={barcodeInputRef}
                  placeholder="Código de barras (F7)"
                  className={`pl-10 h-9 font-mono text-xs transition-colors ${barcodeFlash ? "border-primary bg-primary/5" : ""}`}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const val = (e.target as HTMLInputElement).value.trim();
                      if (val.length >= 2) {
                        processBarcode(val);
                        (e.target as HTMLInputElement).value = "";
                      }
                    }
                  }}
                />
              </div>
            </div>

            <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-none">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                  !selectedCategory ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"
                }`}
              >
                Todos
              </button>
              {categories.map(c => (
                <button
                  key={c.id}
                  onClick={() => setSelectedCategory(c.id === selectedCategory ? null : c.id)}
                  className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                    c.id === selectedCategory ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>

          {/* Product grid */}
          <div className="flex-1 overflow-auto px-4 pb-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-2">
              {filteredProducts.map(p => {
                const inCart = cart.find(c => c.product.id === p.id);
                const cartQty = cart.filter(c => c.product.id === p.id).reduce((sum, c) => sum + c.quantity, 0);
                const hasAddons = allAddons.some(a => a.product_id === p.id && a.is_available);
                return (
                  <motion.button
                    key={p.id}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => addToCart(p)}
                    className={`relative bg-card border rounded-xl p-2.5 text-left transition-all hover:shadow-md ${
                      inCart ? "border-primary ring-1 ring-primary/30" : "border-border"
                    }`}
                  >
                    {p.image_url ? (
                      <div className="w-full aspect-square rounded-lg overflow-hidden mb-2 bg-secondary">
                        <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                      </div>
                    ) : (
                      <div className="w-full aspect-square rounded-lg mb-2 bg-secondary flex items-center justify-center">
                        <UtensilsCrossed className="h-8 w-8 text-muted-foreground/30" />
                      </div>
                    )}
                    <p className="text-xs font-medium line-clamp-2 leading-tight">{p.name}</p>
                    <div className="flex items-center gap-1 mt-0.5">
                      <p className="text-sm font-bold text-primary">{formatCurrency(p.price)}</p>
                      {hasAddons && (
                        <Badge variant="outline" className="text-[8px] px-1 py-0 h-3.5 border-primary/40 text-primary">
                          + adicionais
                        </Badge>
                      )}
                    </div>

                    {cartQty > 0 && (
                      <div className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shadow-lg">
                        {cartQty}
                      </div>
                    )}
                  </motion.button>
                );
              })}
            </div>

            {filteredProducts.length === 0 && (
              <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
                <Search className="h-10 w-10 mb-3 opacity-30" />
                <p className="text-sm">Nenhum produto encontrado</p>
              </div>
            )}
          </div>
        </div>

        {/* Right: Cart */}
        <aside className="w-[380px] border-l border-border bg-card flex flex-col">
          <div className="h-12 border-b border-border flex items-center justify-between px-4">
            <div className="flex items-center gap-2">
              <ShoppingCart className="h-4 w-4 text-primary" />
              <h2 className="font-bold text-sm">Carrinho</h2>
              {cart.length > 0 && (
                <Badge variant="secondary" className="text-[10px] h-5">{cart.reduce((s, c) => s + c.quantity, 0)} itens</Badge>
              )}
            </div>
            <div className="flex items-center gap-1">
              {cart.length > 0 && (
                <>
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={holdCurrentOrder} title="Em espera (F10)">
                    <Pause className="h-3 w-3" />
                  </Button>
                  <button onClick={() => setCart([])} className="text-xs text-destructive hover:underline ml-1">
                    Limpar
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Customer name */}
          <div className="px-3 py-2 border-b border-border">
            <Input
              placeholder="Nome do cliente (opcional)"
              className="h-7 text-xs"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
            />
          </div>

          {/* Cart items */}
          <div className="flex-1 overflow-auto px-3 py-2 space-y-2">
            <AnimatePresence>
              {cart.map((c, idx) => (
                <motion.div
                  key={`${c.product.id}-${idx}`}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="bg-secondary/50 rounded-lg p-2.5 space-y-1.5"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{c.product.name}</p>
                      <p className="text-[10px] text-muted-foreground">{formatCurrency(c.product.price)} un.</p>
                    </div>
                    <p className="text-xs font-bold shrink-0">{formatCurrency(getCartItemTotal(c))}</p>
                  </div>

                  {/* Addons display */}
                  {c.addons.length > 0 && (
                    <div className="pl-2 border-l-2 border-primary/30 space-y-0.5">
                      {c.addons.map(a => (
                        <div key={a.addon.id} className="flex justify-between text-[10px] text-muted-foreground">
                          <span>+ {a.quantity}x {a.addon.name}</span>
                          <span>{formatCurrency(a.addon.price * a.quantity)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => updateQty(idx, -1)}
                        className="w-6 h-6 rounded bg-card border border-border flex items-center justify-center hover:bg-destructive/10"
                      >
                        {c.quantity === 1 ? <Trash2 className="h-2.5 w-2.5 text-destructive" /> : <Minus className="h-2.5 w-2.5" />}
                      </button>
                      <span className="w-7 text-center text-xs font-bold">{c.quantity}</span>
                      <button
                        onClick={() => updateQty(idx, 1)}
                        className="w-6 h-6 rounded bg-card border border-border flex items-center justify-center hover:bg-primary/10"
                      >
                        <Plus className="h-2.5 w-2.5" />
                      </button>
                    </div>
                    <button onClick={() => removeFromCart(idx)} className="text-[10px] text-destructive hover:underline">
                      Remover
                    </button>
                  </div>

                  {editingNotes === `${idx}` ? (
                    <Input
                      placeholder="Observação..."
                      className="h-6 text-[10px]"
                      value={c.notes}
                      onChange={(e) => updateNotes(idx, e.target.value)}
                      onBlur={() => setEditingNotes(null)}
                      autoFocus
                    />
                  ) : (
                    <button
                      onClick={() => setEditingNotes(`${idx}`)}
                      className="text-[10px] text-muted-foreground hover:text-foreground"
                    >
                      {c.notes || "+ Obs"}
                    </button>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>

            {cart.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <ShoppingCart className="h-10 w-10 mb-2 opacity-20" />
                <p className="text-xs">Carrinho vazio</p>
                <p className="text-[10px] mt-0.5">Clique em um produto para adicionar</p>
              </div>
            )}
          </div>

          {/* Footer */}
          {cart.length > 0 && (
            <div className="border-t border-border p-3 space-y-2">
              <div className="space-y-1">
                <div className="flex justify-between items-center text-xs">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>{formatCurrency(subtotal)}</span>
                </div>
                {discountAmount > 0 && (
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-emerald-600 flex items-center gap-1">
                      <Tag className="h-3 w-3" />
                      Desconto {discountType === "percent" ? `(${discountValue}%)` : ""}
                    </span>
                    <span className="text-emerald-600">-{formatCurrency(discountAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="font-bold text-base">Total</span>
                  <span className="font-bold text-base text-primary">{formatCurrency(total)}</span>
                </div>
              </div>

              <div className="flex gap-1.5">
                <Button variant="outline" size="sm" className="h-8 text-xs flex-1" onClick={() => setShowDiscountDialog(true)}>
                  <Percent className="h-3 w-3 mr-1" />
                  Desconto
                </Button>
                <Button variant="outline" size="sm" className="h-8 text-xs flex-1" onClick={() => setShowSplitPayment(true)}>
                  <SplitSquareVertical className="h-3 w-3 mr-1" />
                  Dividir
                </Button>
              </div>

              {!paymentStep ? (
                <div className="space-y-1.5">
                  {orderType === "dine_in" && (
                    <Button
                      variant="outline"
                      className="w-full h-10 text-sm font-bold border-primary/50 text-primary hover:bg-primary/10"
                      disabled={processingPayment}
                      onClick={() => {
                        if (!tableNumber) {
                          toast({ title: "Informe o número da mesa", variant: "destructive" });
                          return;
                        }
                        sendToTab();
                      }}
                    >
                      {processingPayment ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ClipboardList className="h-4 w-4 mr-2" />}
                      Enviar p/ Comanda (Mesa {tableNumber || "?"})
                    </Button>
                  )}
                  <Button
                    className="w-full h-11 bg-primary text-primary-foreground text-sm font-bold"
                    onClick={() => {
                      if (orderType === "dine_in" && !tableNumber) {
                        toast({ title: "Informe o número da mesa", variant: "destructive" });
                        return;
                      }
                      setPaymentStep(true);
                    }}
                  >
                    Finalizar Pedido (F9)
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-center">Forma de pagamento</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {([
                      { method: "cash" as PaymentMethod, label: "1 · Dinheiro", icon: DollarSign, color: "hover:border-emerald-500 hover:bg-emerald-500/5" },
                      { method: "credit_card" as PaymentMethod, label: "2 · Crédito", icon: CreditCard, color: "hover:border-blue-500 hover:bg-blue-500/5" },
                      { method: "debit_card" as PaymentMethod, label: "3 · Débito", icon: CreditCard, color: "hover:border-purple-500 hover:bg-purple-500/5" },
                      { method: "pix" as PaymentMethod, label: "4 · PIX", icon: QrCode, color: "hover:border-cyan-500 hover:bg-cyan-500/5" },
                    ]).map(pm => (
                      <Button
                        key={pm.method}
                        variant="outline"
                        className={`h-12 flex-col gap-0.5 ${pm.color}`}
                        disabled={processingPayment}
                        onClick={() => handlePayment(pm.method)}
                      >
                        {processingPayment && paymentMethod === pm.method ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <pm.icon className="h-4 w-4" />
                        )}
                        <span className="text-[10px]">{pm.label}</span>
                      </Button>
                    ))}
                  </div>
                  <Button variant="ghost" size="sm" className="w-full text-xs h-7" onClick={() => setPaymentStep(false)}>
                    Voltar (Esc)
                  </Button>
                </div>
              )}
            </div>
          )}
        </aside>
      </div>

      {/* ===== DIALOGS ===== */}

      {/* Cash Payment / Change Calculator Dialog */}
      <Dialog open={showCashDialog} onOpenChange={(open) => {
        if (!open) { setShowCashDialog(false); setCashReceived(""); setPendingCashOrder(false); }
      }}>
        <DialogContent className="sm:max-w-xs">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Banknote className="h-5 w-5" /> Pagamento em Dinheiro</DialogTitle>
            <DialogDescription>Informe o valor recebido para calcular o troco</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Total do pedido</p>
              <p className="text-2xl font-bold text-primary">{formatCurrency(total)}</p>
            </div>

            <div className="space-y-2">
              <Label>Valor recebido</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0,00"
                value={cashReceived}
                onChange={(e) => setCashReceived(e.target.value)}
                className="text-center text-lg h-12 font-bold"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") confirmCashPayment();
                }}
              />
            </div>

            {/* Quick amount buttons */}
            <div className="grid grid-cols-4 gap-1.5">
              {[5, 10, 20, 50, 100, 200].map(v => (
                <Button
                  key={v}
                  variant="outline"
                  size="sm"
                  className="text-xs h-8"
                  onClick={() => setCashReceived(String(v))}
                >
                  {v}
                </Button>
              ))}
              <Button
                variant="outline"
                size="sm"
                className="text-xs h-8 col-span-2"
                onClick={() => setCashReceived(String(Math.ceil(total)))}
              >
                Exato
              </Button>
            </div>

            {/* Change display */}
            {cashReceived && parseFloat(cashReceived) >= total && (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Coins className="h-5 w-5 text-emerald-600" />
                  <span className="text-sm font-semibold text-emerald-700">Troco</span>
                </div>
                <p className="text-3xl font-bold text-emerald-600">
                  {formatCurrency(parseFloat(cashReceived) - total)}
                </p>
              </div>
            )}
            {cashReceived && parseFloat(cashReceived) > 0 && parseFloat(cashReceived) < total && (
              <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-3 text-center">
                <p className="text-sm text-destructive font-medium">
                  Faltam {formatCurrency(total - parseFloat(cashReceived))}
                </p>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setShowCashDialog(false); setCashReceived(""); setPendingCashOrder(false); }}>
              Cancelar
            </Button>
            <Button
              onClick={confirmCashPayment}
              disabled={!cashReceived || parseFloat(cashReceived) < total || processingPayment}
            >
              {processingPayment ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Open Cash Register Dialog */}
      <Dialog open={showOpenCashDialog} onOpenChange={setShowOpenCashDialog}>
        <DialogContent className="sm:max-w-xs">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Unlock className="h-4 w-4" /> Abrir Caixa</DialogTitle>
            <DialogDescription>Informe o saldo inicial do caixa</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Saldo Inicial (R$)</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0,00"
                value={openingBalance}
                onChange={(e) => setOpeningBalance(e.target.value)}
                className="text-center text-lg h-12 font-bold"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOpenCashDialog(false)}>Cancelar</Button>
            <Button onClick={openCashRegister}>
              <Unlock className="h-4 w-4 mr-2" />
              Abrir Caixa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close Cash Register Dialog */}
      <Dialog open={showCloseCashDialog} onOpenChange={setShowCloseCashDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Lock className="h-4 w-4" /> Fechar Caixa</DialogTitle>
            <DialogDescription>Confira os valores e feche o caixa</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Summary */}
            <div className="bg-secondary/50 rounded-lg p-3 space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Saldo Inicial</span><span>{formatCurrency(cashSummary.opening)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Vendas ({cashSummary.salesCount})</span><span className="text-emerald-600">+{formatCurrency(cashSummary.totalSales)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Suprimentos</span><span className="text-emerald-600">+{formatCurrency(cashSummary.totalSuprimento)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Sangrias</span><span className="text-destructive">-{formatCurrency(cashSummary.totalSangria)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Trocos</span><span className="text-destructive">-{formatCurrency(cashSummary.totalChangeGiven)}</span></div>
              <div className="border-t border-border pt-1.5 flex justify-between font-bold">
                <span>Saldo Esperado</span>
                <span className="text-primary">{formatCurrency(cashSummary.expected)}</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Saldo Real no Caixa (R$)</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0,00"
                value={closingBalance}
                onChange={(e) => setClosingBalance(e.target.value)}
                className="text-center text-lg h-12 font-bold"
                autoFocus
              />
            </div>

            {closingBalance && (
              <div className={`rounded-lg p-3 text-center ${
                parseFloat(closingBalance) >= cashSummary.expected
                  ? "bg-emerald-500/10 border border-emerald-500/30"
                  : "bg-destructive/10 border border-destructive/30"
              }`}>
                <p className="text-xs text-muted-foreground">Diferença</p>
                <p className={`text-xl font-bold ${
                  parseFloat(closingBalance) >= cashSummary.expected ? "text-emerald-600" : "text-destructive"
                }`}>
                  {(parseFloat(closingBalance) - cashSummary.expected) >= 0 ? "+" : ""}
                  {formatCurrency(parseFloat(closingBalance) - cashSummary.expected)}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Observações (opcional)</Label>
              <Textarea
                placeholder="Notas sobre o fechamento..."
                value={closingNotes}
                onChange={(e) => setClosingNotes(e.target.value)}
                className="text-sm"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => {
              printClosingReport();
            }}>
              <Printer className="h-4 w-4 mr-2" />
              Imprimir
            </Button>
            <Button variant="destructive" onClick={closeCashRegister}>
              <Lock className="h-4 w-4 mr-2" />
              Fechar Caixa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cash Movement Dialog (Sangria / Suprimento) */}
      <Dialog open={showCashMovementDialog} onOpenChange={setShowCashMovementDialog}>
        <DialogContent className="sm:max-w-xs">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {cashMovementType === "sangria" ? (
                <><ArrowDownCircle className="h-4 w-4 text-destructive" /> Sangria</>
              ) : (
                <><ArrowUpCircle className="h-4 w-4 text-emerald-600" /> Suprimento</>
              )}
            </DialogTitle>
            <DialogDescription>
              {cashMovementType === "sangria" ? "Retirada de dinheiro do caixa" : "Entrada de dinheiro no caixa"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Valor (R$)</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0,00"
                value={cashMovementAmount}
                onChange={(e) => setCashMovementAmount(e.target.value)}
                className="text-center text-lg h-12 font-bold"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label>Descrição (opcional)</Label>
              <Input
                placeholder={cashMovementType === "sangria" ? "Ex: Pagamento de fornecedor" : "Ex: Troco extra"}
                value={cashMovementDesc}
                onChange={(e) => setCashMovementDesc(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCashMovementDialog(false)}>Cancelar</Button>
            <Button onClick={addCashMovement} variant={cashMovementType === "sangria" ? "destructive" : "default"}>
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cash Report Dialog */}
      <Dialog open={showCashReportDialog} onOpenChange={setShowCashReportDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-4 w-4" /> Relatório do Caixa (F6)
            </DialogTitle>
            <DialogDescription>
              {cashSession ? `Aberto em ${new Date(cashSession.opened_at).toLocaleString("pt-BR")}` : "Nenhum caixa aberto"}
            </DialogDescription>
          </DialogHeader>

          {cashSession ? (
            <div className="space-y-4">
              {/* Summary cards */}
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-secondary/50 rounded-lg p-3 text-center">
                  <p className="text-[10px] text-muted-foreground">Saldo Inicial</p>
                  <p className="text-sm font-bold">{formatCurrency(cashSummary.opening)}</p>
                </div>
                <div className="bg-emerald-500/10 rounded-lg p-3 text-center">
                  <p className="text-[10px] text-emerald-700">Vendas</p>
                  <p className="text-sm font-bold text-emerald-600">{formatCurrency(cashSummary.totalSales)}</p>
                </div>
                <div className="bg-destructive/10 rounded-lg p-3 text-center">
                  <p className="text-[10px] text-destructive">Sangrias</p>
                  <p className="text-sm font-bold text-destructive">{formatCurrency(cashSummary.totalSangria)}</p>
                </div>
                <div className="bg-primary/10 rounded-lg p-3 text-center">
                  <p className="text-[10px] text-primary">Saldo Esperado</p>
                  <p className="text-sm font-bold text-primary">{formatCurrency(cashSummary.expected)}</p>
                </div>
              </div>

              {/* Movements list */}
              {cashMovements.length > 0 && (
                <div className="space-y-1 max-h-48 overflow-auto">
                  <p className="text-xs font-semibold text-muted-foreground">Movimentações</p>
                  {cashMovements.map(m => {
                    const icons: Record<string, string> = { sale: "💰", sangria: "💸", suprimento: "💵", change_given: "🪙" };
                    const labels: Record<string, string> = { sale: "Venda", sangria: "Sangria", suprimento: "Suprimento", change_given: "Troco" };
                    const isPositive = m.type === "sale" || m.type === "suprimento";
                    return (
                      <div key={m.id} className="flex items-center gap-2 text-xs bg-secondary/30 rounded px-2 py-1.5">
                        <span>{icons[m.type] || "📝"}</span>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium">{labels[m.type] || m.type}</p>
                          {m.description && <p className="text-[10px] text-muted-foreground truncate">{m.description}</p>}
                        </div>
                        <span className={`font-bold shrink-0 ${isPositive ? "text-emerald-600" : "text-destructive"}`}>
                          {isPositive ? "+" : "-"}{formatCurrency(m.amount)}
                        </span>
                        <span className="text-[9px] text-muted-foreground shrink-0">
                          {new Date(m.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Lock className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Nenhum caixa aberto</p>
            </div>
          )}

          <DialogFooter className="gap-2">
            {cashSession && (
              <Button variant="destructive" onClick={() => { setShowCashReportDialog(false); setShowCloseCashDialog(true); }}>
                <Lock className="h-4 w-4 mr-2" />
                Fechar Caixa
              </Button>
            )}
            {!cashSession && (
              <Button onClick={() => { setShowCashReportDialog(false); setShowOpenCashDialog(true); }}>
                <Unlock className="h-4 w-4 mr-2" />
                Abrir Caixa
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Discount Dialog */}
      <Dialog open={showDiscountDialog} onOpenChange={setShowDiscountDialog}>
        <DialogContent className="sm:max-w-xs">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Percent className="h-4 w-4" /> Desconto</DialogTitle>
            <DialogDescription>Aplique um desconto ao pedido</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Button variant={discountType === "percent" ? "default" : "outline"} size="sm" className="flex-1" onClick={() => setDiscountType("percent")}>%</Button>
              <Button variant={discountType === "fixed" ? "default" : "outline"} size="sm" className="flex-1" onClick={() => setDiscountType("fixed")}>R$</Button>
            </div>
            <Input
              type="number"
              placeholder={discountType === "percent" ? "Ex: 10" : "Ex: 5.00"}
              value={discountValue || ""}
              onChange={(e) => setDiscountValue(Number(e.target.value) || 0)}
              autoFocus
            />
            {discountValue > 0 && (
              <p className="text-sm text-center text-muted-foreground">
                Desconto: <strong className="text-emerald-600">{formatCurrency(discountType === "percent" ? subtotal * (discountValue / 100) : discountValue)}</strong>
              </p>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setDiscountValue(0); setShowDiscountDialog(false); }}>Remover</Button>
            <Button onClick={() => setShowDiscountDialog(false)}>Aplicar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Held Orders Dialog */}
      <Dialog open={showHeldOrders} onOpenChange={setShowHeldOrders}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Pause className="h-4 w-4" /> Pedidos em Espera (F3)</DialogTitle>
            <DialogDescription>Pedidos pausados aguardando retomada</DialogDescription>
          </DialogHeader>
          {heldOrders.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">Nenhum pedido em espera</p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-auto">
              {heldOrders.map(h => (
                <div key={h.id} className="bg-secondary/50 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">{h.name}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {h.cart.reduce((s, c) => s + c.quantity, 0)} itens • {formatCurrency(h.cart.reduce((s, c) => s + c.product.price * c.quantity, 0))} • {h.orderType === "dine_in" ? `Mesa ${h.tableNumber}` : "Balcão"}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" className="h-7 text-xs" onClick={() => resumeHeldOrder(h.id)}>
                        <Play className="h-3 w-3 mr-1" /> Retomar
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 text-xs text-destructive" onClick={() => removeHeldOrder(h.id)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Tables Panel Dialog */}
      <Dialog open={showTablesPanel} onOpenChange={setShowTablesPanel}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-4 w-4" /> Mesas Ativas (F4)
              <Button variant="ghost" size="sm" className="ml-auto h-7" onClick={() => restaurantId && loadActiveTabs(restaurantId)}>
                <RefreshCw className="h-3 w-3" />
              </Button>
            </DialogTitle>
            <DialogDescription>Comandas abertas nas mesas</DialogDescription>
          </DialogHeader>

          {/* New tab shortcut */}
          <div className="flex items-center gap-2 pb-2 border-b border-border">
            <Input
              type="number"
              placeholder="Nº da mesa"
              className="w-24 h-8 text-xs"
              id="new-tab-table-number"
            />
            <Button
              size="sm"
              className="h-8 text-xs"
              onClick={() => {
                const input = document.getElementById("new-tab-table-number") as HTMLInputElement;
                const num = Number(input?.value);
                if (!num) {
                  toast({ title: "Informe o número da mesa", variant: "destructive" });
                  return;
                }
                setOrderType("dine_in");
                setTableNumber(num);
                setShowTablesPanel(false);
                toast({ title: `Mesa ${num} selecionada`, description: "Adicione itens e clique em 'Enviar p/ Comanda'" });
              }}
            >
              <ClipboardList className="h-3.5 w-3.5 mr-1" />
              Nova Comanda
            </Button>
          </div>

          {activeTabs.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">Nenhuma mesa com comanda aberta</p>
          ) : (
            <div className="grid grid-cols-2 gap-2 max-h-96 overflow-auto">
              {activeTabs.map(tab => (
                <div key={tab.id} className="bg-secondary/50 rounded-lg p-3 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="text-xs">Mesa {tab.table_number}</Badge>
                    <span className="text-xs font-bold text-primary">{formatCurrency(tab.total)}</span>
                  </div>
                  <div className="space-y-0.5">
                    {tab.items.slice(0, 3).map((item, i) => (
                      <p key={i} className="text-[10px] text-muted-foreground truncate">{item.quantity}x {item.product_name}</p>
                    ))}
                    {tab.items.length > 3 && <p className="text-[10px] text-muted-foreground">+{tab.items.length - 3} itens</p>}
                  </div>
                  <div className="flex gap-1 pt-1">
                    <Button size="sm" variant="outline" className="h-6 text-[10px] flex-1" onClick={() => {
                      setOrderType("dine_in"); setTableNumber(tab.table_number); setShowTablesPanel(false);
                    }}>Selecionar</Button>
                    <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => navigate("/mesas-ativas")}>Gerenciar</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Recent Orders Dialog */}
      <Dialog open={showRecentOrders} onOpenChange={setShowRecentOrders}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ReceiptText className="h-4 w-4" /> Pedidos de Hoje (F5)</DialogTitle>
            <DialogDescription>Últimos pedidos realizados</DialogDescription>
          </DialogHeader>
          {recentOrders.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">Nenhum pedido hoje</p>
          ) : (
            <div className="space-y-1.5 max-h-96 overflow-auto">
              {recentOrders.map(o => {
                const statusColors: Record<string, string> = {
                  received: "bg-blue-500/10 text-blue-600", preparing: "bg-amber-500/10 text-amber-600",
                  ready: "bg-emerald-500/10 text-emerald-600", completed: "bg-muted text-muted-foreground",
                  cancelled: "bg-destructive/10 text-destructive",
                };
                const statusLabels: Record<string, string> = {
                  received: "Recebido", preparing: "Preparando", ready: "Pronto", completed: "Concluído", cancelled: "Cancelado",
                };
                const pm: Record<string, string> = { pix: "PIX", credit_card: "Crédito", debit_card: "Débito", cash: "Dinheiro" };
                return (
                  <div key={o.id} className="flex items-center gap-3 bg-secondary/30 rounded-lg px-3 py-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-xs font-mono font-medium">#{o.id.slice(0, 6)}</p>
                        <Badge className={`text-[9px] h-4 ${statusColors[o.status] || ""}`}>{statusLabels[o.status] || o.status}</Badge>
                      </div>
                      <p className="text-[10px] text-muted-foreground truncate">
                        {o.customer_name || "—"} • {o.order_type === "dine_in" ? `Mesa ${o.table_number}` : "Balcão"} • {pm[o.payment_method] || o.payment_method}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs font-bold">{formatCurrency(o.total)}</p>
                      <p className="text-[9px] text-muted-foreground">{new Date(o.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Split Payment Dialog */}
      <Dialog open={showSplitPayment} onOpenChange={setShowSplitPayment}>
        <DialogContent className="sm:max-w-xs">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><SplitSquareVertical className="h-4 w-4" /> Dividir Conta</DialogTitle>
            <DialogDescription>Divida o valor entre as pessoas</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-4">
              <Button variant="outline" size="sm" onClick={() => setSplitParts(Math.max(2, splitParts - 1))}><Minus className="h-4 w-4" /></Button>
              <span className="text-3xl font-bold">{splitParts}</span>
              <Button variant="outline" size="sm" onClick={() => setSplitParts(Math.min(20, splitParts + 1))}><Plus className="h-4 w-4" /></Button>
            </div>
            <div className="text-center space-y-1">
              <p className="text-sm text-muted-foreground">Total: {formatCurrency(total)}</p>
              <p className="text-2xl font-bold text-primary">
                {formatCurrency(total / splitParts)} <span className="text-sm font-normal text-muted-foreground">/ pessoa</span>
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSplitPayment(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Keyboard Shortcuts Dialog */}
      <Dialog open={showShortcuts} onOpenChange={setShowShortcuts}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Keyboard className="h-4 w-4" /> Atalhos do Teclado</DialogTitle>
            <DialogDescription>Use atalhos para agilizar o atendimento</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {[
              ["F1", "Atalhos do teclado"],
              ["F2", "Buscar produto"],
              ["F3", "Pedidos em espera"],
              ["F4", "Mesas ativas"],
              ["F5", "Pedidos recentes"],
              ["F6", "Relatório do caixa"],
              ["F7", "Código de barras"],
              ["F8", "Desconto"],
              ["F9", "Finalizar pedido"],
              ["F10", "Colocar em espera"],
              ["1-4", "Pagamento (na tela de pagamento)"],
              ["Esc", "Voltar / Fechar"],
            ].map(([key, desc]) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{desc}</span>
                <kbd className="bg-secondary px-2 py-0.5 rounded text-xs font-mono font-bold">{key}</kbd>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Addon Selection Dialog */}
      <Dialog open={!!addonDialogProduct} onOpenChange={(open) => { if (!open) setAddonDialogProduct(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Adicionais — {addonDialogProduct?.name}
            </DialogTitle>
            <DialogDescription>
              Selecione os adicionais desejados para este produto
            </DialogDescription>
          </DialogHeader>

          {addonDialogProduct && (
            <div className="space-y-3 max-h-[50vh] overflow-auto">
              {getProductAddons(addonDialogProduct.id).map(addon => {
                const qty = addonSelections[addon.id] || 0;
                return (
                  <div key={addon.id} className="flex items-center justify-between bg-secondary/50 rounded-lg p-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{addon.name}</p>
                      <p className="text-xs text-primary font-bold">+ {formatCurrency(addon.price)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setAddonSelections(prev => ({ ...prev, [addon.id]: Math.max(0, qty - 1) }))}
                        disabled={qty === 0}
                        className="w-7 h-7 rounded-full bg-card border border-border flex items-center justify-center hover:bg-destructive/10 disabled:opacity-30"
                      >
                        <Minus className="h-3 w-3" />
                      </button>
                      <span className="w-6 text-center text-sm font-bold">{qty}</span>
                      <button
                        onClick={() => setAddonSelections(prev => ({ ...prev, [addon.id]: Math.min(addon.max_qty, qty + 1) }))}
                        disabled={qty >= addon.max_qty}
                        className="w-7 h-7 rounded-full bg-card border border-border flex items-center justify-center hover:bg-primary/10 disabled:opacity-30"
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                );
              })}

              {getProductAddons(addonDialogProduct.id).length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">Nenhum adicional disponível</p>
              )}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                // Add without addons
                if (addonDialogProduct) {
                  setCart(prev => {
                    const existing = prev.find(c => c.product.id === addonDialogProduct.id && c.addons.length === 0);
                    if (existing) return prev.map(c => c.product.id === addonDialogProduct.id && c.addons.length === 0 ? { ...c, quantity: c.quantity + 1 } : c);
                    return [...prev, { product: addonDialogProduct, quantity: 1, notes: "", addons: [] }];
                  });
                  setAddonDialogProduct(null);
                }
              }}
            >
              Sem adicionais
            </Button>
            <Button
              className="flex-1"
              onClick={confirmAddonsToCart}
              disabled={Object.values(addonSelections).every(v => v === 0)}
            >
              <Check className="h-4 w-4 mr-1" />
              Confirmar{Object.values(addonSelections).some(v => v > 0) && (
                <span className="ml-1">
                  (+{formatCurrency(
                    Object.entries(addonSelections).reduce((sum, [id, qty]) => {
                      const addon = allAddons.find(a => a.id === id);
                      return sum + (addon ? addon.price * qty : 0);
                    }, 0)
                  )})
                </span>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PDV;
